import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	InvoicesTable,
	PageHeader,
	PaymentMethodList,
	PlanSummaryRow,
	SectionHeader,
	SettingsLayout,
	UsageCard,
} from "@/components"
import PaymentMethodModal from "../components-legacy/PaymentMethodModal.jsx"
import { api } from "../system/api.js"
import { paywallTrigger } from "../system/paywallTrigger.js"
import { STATIC_PLAN_DATA } from "../data/Plans.js"

function formatDate(input) {
	const date = new Date(input.replace(" ", "T"))
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	})
}

function formatDateShort(input) {
	const date = new Date(input)
	return date.toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
		year: "numeric",
	})
}

function formatInvoiceStatus(status) {
	switch (status) {
		case 'paid': return 'Paid'
		case 'open': return 'Pending'
		case 'draft': return 'Draft'
		case 'failed': return 'Failed'
		case 'void': return 'Cancelled'
		case 'overdue': return 'Overdue'
		default: return status
	}
}

function getStatusTone(status) {
	switch (status) {
		case 'paid':
			return 'success'
		case 'failed':
		case 'overdue':
			return 'danger'
		case 'draft':
		case 'void':
			return 'muted'
		default:
			return undefined
	}
}

export default function Settings() {
	const navigate = useNavigate()

	// Subscription state
	const [subLoading, setSubLoading] = useState(true)
	const [subError, setSubError] = useState(null)
	const [subscription, setSubscription] = useState(null)
	const [planDetails, setPlanDetails] = useState(null)
	const [cancelError, setCancelError] = useState(null)
	const [retryLoading, setRetryLoading] = useState(false)
	const [showCancelDialog, setShowCancelDialog] = useState(false)

	// Payment methods state
	const [methods, setMethods] = useState([])
	const [methodsLoading, setMethodsLoading] = useState(true)
	const [methodsError, setMethodsError] = useState(null)
	const [showAddCardDialog, setShowAddCardDialog] = useState(false)

	// Invoices state
	const [invoices, setInvoices] = useState([])
	const [invoicesLoading, setInvoicesLoading] = useState(true)

	useEffect(() => {
		api('subscription').then(res => {
			setSubscription(res)
			setPlanDetails(STATIC_PLAN_DATA[res.slug])
		}).catch(() => {
			setSubError('Failed to load subscription details.')
		}).finally(() => {
			setSubLoading(false)
		})

		loadPaymentMethods()

		api('user/invoices').then(res => {
			setInvoices(res || [])
		}).catch(() => {
			setInvoices([])
		}).finally(() => {
			setInvoicesLoading(false)
		})
	}, [])

	const loadPaymentMethods = async () => {
		setMethodsLoading(true)
		setMethodsError(null)
		try {
			const data = await api('payment_methods')
			setMethods(data)
		} catch {
			setMethodsError('Failed to load payment methods.')
		} finally {
			setMethodsLoading(false)
		}
	}

	const cancelSubscription = async () => {
		try {
			await api('subscription/cancel')
			window.location.reload()
		} catch {
			setCancelError('Failed to cancel subscription.')
		} finally {
			setShowCancelDialog(false)
		}
	}

	const revertCancelSubscription = () => {
		api('subscription/cancel/revert').then(() => {
			window.location.reload()
		}).catch(() => {
			setCancelError('Failed to revert cancelled subscription.')
		})
	}

	const retrySponsorEligibility = () => {
		setRetryLoading(true)
		setCancelError(null)
		api('subscription/retry/sponsor', {
			userProductId: subscription.userProductId,
		}).then(() => {
			window.location.reload()
		}).catch((err) => {
			if (err.errorCode === 'SPONSOR_ELIGIBILITY_ERROR' && err.userMessage) {
				setCancelError(err.userMessage)
			} else {
				setCancelError('Eligibility check failed. Please ensure you meet the sponsor requirements.')
			}
		}).finally(() => {
			setRetryLoading(false)
		})
	}

	const handleSetDefault = async (method) => {
		try {
			await api('payment_method_default', { payment_method_id: method.id })
			await loadPaymentMethods()
		} catch {
			alert('Failed to set default payment method.')
		}
	}

	const handleDeleteMethod = async (method) => {
		try {
			await api('payment_method_detach', { payment_method_id: method.id })
			await loadPaymentMethods()
		} catch (err) {
			if (err.errorCode === 'CANNOT_DELETE_DEFAULT_PAYMENT_METHOD') {
				alert('Cannot remove the default payment method. Please set another card as default first.')
			} else {
				alert('Failed to remove payment method.')
			}
		}
	}

	const handlePayInvoice = (invoice) => {
		const invoiceId = invoice.id.replace(/^#/, '')
		navigate(`/payment?type=invoice&id=${invoiceId}`)
	}

	// Map API payment methods to the v1 PaymentMethod type
	const mappedMethods = methods.map(m => ({
		id: m.id,
		brand: m.brand.charAt(0).toUpperCase() + m.brand.slice(1),
		last4: m.last4,
		expiry: `${String(m.exp_month).padStart(2, '0')}/${m.exp_year}`,
		isDefault: m.is_default,
	}))

	// Map API invoices to the v1 Invoice type
	const mappedInvoices = invoices.map(item => ({
		id: `#${item.invoiceId}`,
		amount: `${item.currency} ${item.amount}`,
		date: formatDateShort(item.createdAt),
		status: formatInvoiceStatus(item.status),
		statusTone: getStatusTone(item.status),
		payable: ['failed', 'overdue', 'open'].includes(item.status),
	}))

	// Derived subscription values
	const totalCredits = subscription
		? (subscription.credits !== -1
			? subscription.credits + (subscription.addonCreditsTotal || 0)
			: -1)
		: 0
	const totalCreditsUsed = subscription
		? (subscription.creditsUsage + (subscription.addonCreditsUsage || 0))
		: 0
	const creditsUsagePercent = totalCredits > 0
		? Math.min(100, Math.round((totalCreditsUsed / totalCredits) * 100))
		: 0
	const billingCycleText = subscription?.billingCycle === 'monthly' ? 'Billed Monthly' : 'Billed Annually'
	const creditsResetDate = subscription
		? formatDate(subscription.creditsExpiresAt || subscription.expiresAt)
		: ''

	return (
		<>
			<SettingsLayout onBack={() => navigate(-1)}>
				<PageHeader
					title="Billing & Usage"
					description="Manage your plan, payment methods, and billing history."
					className="mb-8"
				/>

				{/* Usage section */}
				<section className="mb-10">
					<SectionHeader
						title="Usage this billing period"
						description="Where you're at on your current credit allowance."
					/>
					{subLoading ? (
						<div className="h-24 rounded-md border border-border bg-card shimmer-bar" />
					) : subError ? (
						<p className="text-caption text-danger">{subError}</p>
					) : subscription && totalCredits !== -1 ? (
						<UsageCard
							title="This billing period"
							caption={
								subscription.billingCycle !== 'onetime'
									? `Resets on ${creditsResetDate}`
									: 'One-time credits'
							}
							percentUsed={creditsUsagePercent}
							used={totalCreditsUsed}
							total={totalCredits}
							remaining={Math.max(0, totalCredits - totalCreditsUsed)}
							usedLabel="credits used"
							remainingLabel="remaining"
							tone={creditsUsagePercent >= 80 ? "warning" : "primary"}
						/>
					) : subscription ? (
						<div className="rounded-md border border-border bg-card p-5">
							<span className="text-section text-foreground">Unlimited credits</span>
						</div>
					) : null}
				</section>

				{/* Plan section */}
				<section className="mb-10">
					<SectionHeader
						title="Your plan"
						description="Upgrade, downgrade, or cancel anytime."
					/>
					{subLoading ? (
						<div className="h-16 rounded-md border border-border bg-card shimmer-bar" />
					) : subError ? null : subscription ? (
						<>
							<PlanSummaryRow
								planName={`${subscription.name} plan`}
								statusCaption={
									subscription.cancelationRequested
										? `Cancelling on ${formatDate(subscription.expiresAt)}`
										: subscription.slug === 'free'
											? "You're on the free plan. Unlock the full feature set anytime."
											: `Active. ${billingCycleText}.`
								}
								actionSlot={
									!subscription.cancelationRequested ? (
										<Button
											size="sm"
											className="text-label motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100"
											onClick={() => paywallTrigger.open()}
										>
											Change plan
										</Button>
									) : undefined
								}
							/>

							{/* Cancellation scheduled notice + revert */}
							{subscription.cancelationRequested && (
								<div className="mt-3 flex flex-col gap-3">
									<p className="text-caption text-danger">
										Your subscription is scheduled to be cancelled on {formatDate(subscription.expiresAt)}, and you&apos;ll be downgraded to the {subscription.action?.targetProductName || 'Free'} plan.
									</p>
									<Button
										variant="outline"
										size="sm"
										className="text-label w-fit"
										onClick={revertCancelSubscription}
									>
										Revert cancellation
									</Button>
								</div>
							)}

							{/* Cancel subscription trigger */}
							{subscription.slug !== 'free' && !subscription.cancelationRequested && (
								<div className="mt-2">
									<Button
										variant="ghost"
										size="sm"
										className="text-label text-muted-foreground hover:text-destructive"
										onClick={() => setShowCancelDialog(true)}
									>
										Cancel subscription
									</Button>
								</div>
							)}

							{/* Sponsor eligibility retry */}
							{subscription.sponsor && subscription.state === 2 && (
								<div className="mt-3 rounded-md border border-warning-dim bg-warning-dim p-4 flex flex-col gap-3">
									<p className="text-caption text-foreground">
										Your sponsored subscription requires eligibility verification to continue.
									</p>
									<Button
										size="sm"
										className="text-label w-fit"
										onClick={retrySponsorEligibility}
										disabled={retryLoading}
									>
										{retryLoading ? 'Checking...' : 'Check Eligibility Now'}
									</Button>
								</div>
							)}

							{cancelError && (
								<p className="text-caption text-danger mt-2">{cancelError}</p>
							)}
						</>
					) : null}
				</section>

				{/* Payment methods section */}
				<section className="mb-10">
					<SectionHeader
						title="Payment methods"
						description="Manage the cards used for subscription billing."
					/>
					{methodsLoading ? (
						<div className="h-16 rounded-md border border-border bg-card shimmer-bar" />
					) : methodsError ? (
						<p className="text-caption text-danger">{methodsError}</p>
					) : (
						<PaymentMethodList
							methods={mappedMethods}
							onSetDefault={handleSetDefault}
							onDelete={handleDeleteMethod}
							onAdd={() => setShowAddCardDialog(true)}
						/>
					)}
				</section>

				{/* Billing history section */}
				<section className="pb-6">
					<SectionHeader
						title="Billing history"
						description="View invoices for previous subscription charges."
					/>
					{invoicesLoading ? (
						<div className="h-24 rounded-md border border-border bg-card shimmer-bar" />
					) : mappedInvoices.length === 0 ? (
						<div className="rounded-md border border-border bg-card p-5">
							<p className="text-caption text-muted-foreground">No invoices found.</p>
						</div>
					) : (
						<InvoicesTable
							invoices={mappedInvoices}
							showCard={false}
							onPay={handlePayInvoice}
							payAriaLabel={(inv) => `Pay invoice ${inv.id}`}
						/>
					)}
				</section>
			</SettingsLayout>

			{/* Cancel subscription confirmation dialog */}
			<Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
				<DialogContent className="v1 sm:max-w-sm">
					<DialogHeader>
						<DialogTitle className="text-section text-foreground">Cancel subscription?</DialogTitle>
						<DialogDescription className="text-body text-muted-foreground">
							Are you sure? You&apos;ll lose access to premium features at the end of your billing period.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="gap-2">
						<Button
							variant="ghost"
							size="sm"
							className="text-label"
							onClick={() => setShowCancelDialog(false)}
						>
							Keep subscription
						</Button>
						<Button
							variant="destructive"
							size="sm"
							className="text-label"
							onClick={cancelSubscription}
						>
							Cancel subscription
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Add payment method dialog */}
			<Dialog
				open={showAddCardDialog}
				onOpenChange={(open) => { if (!open) setShowAddCardDialog(false) }}
			>
				<DialogContent className="v1 sm:max-w-lg">
					<PaymentMethodModal
						onClose={() => setShowAddCardDialog(false)}
						onSuccess={() => {
							setShowAddCardDialog(false)
							loadPaymentMethods()
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	)
}
